import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { SuggestedFriend } from '@/types/friendship';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import React, { useCallback } from 'react';
import { Pressable, View } from 'react-native';

interface SuggestedFriendCardProps {
  suggestion: SuggestedFriend;
  onAddFriend: (userId: string) => void;
  isPending?: boolean;
}

export const SuggestedFriendCard = React.memo<SuggestedFriendCardProps>(({
  suggestion,
  onAddFriend,
  isPending,
}) => {
  const displayName = suggestion.name || suggestion.username || 'User';
  const normalizedUsername = (suggestion.displayUsername || suggestion.username || '').replace(/^@+/, '');

  const handlePress = useCallback(() => {
    const identifier = suggestion.username?.replace(/^@+/, '');
    if (identifier) {
      router.push({
        pathname: '/(root)/profile/[username]',
        params: { username: identifier },
      });
    }
  }, [suggestion.username]);

  const handleAddFriend = useCallback(() => {
    onAddFriend(suggestion.userId);
  }, [suggestion.userId, onAddFriend]);

  return (
    <View className="mr-4 w-40">
      <View className="rounded-xl overflow-hidden bg-surface border border-border p-3 items-center">
        <Pressable
          className="items-center w-full"
          onPress={handlePress}
          accessibilityRole="button"
          accessibilityLabel={`View ${displayName}'s profile`}
        >
          <UserAvatar
            uri={suggestion.image}
            name={suggestion.name || suggestion.username}
            size={64}
            decorative
            className="bg-muted"
            style={{ marginBottom: 8 }}
          />

          <Text className="font-semibold text-sm text-foreground text-center" numberOfLines={1}>
            {displayName}
          </Text>

          {normalizedUsername ? (
            <Text className="text-xs text-muted-foreground text-center mb-1" numberOfLines={1}>
              @{normalizedUsername}
            </Text>
          ) : (
            <View className="mb-1" />
          )}

          {suggestion.mutualFriendCount > 0 && (
            <Text className="text-xs text-muted-foreground text-center mb-2" numberOfLines={1}>
              {suggestion.mutualFriendCount} mutual {suggestion.mutualFriendCount === 1 ? 'friend' : 'friends'}
            </Text>
          )}

          {suggestion.mutualFriendCount === 0 && <View className="mb-2" />}
        </Pressable>

        <Button
          variant="secondary"
          size="sm"
          fullWidth
          loading={isPending}
          onPress={handleAddFriend}
          accessibilityLabel={`Add ${displayName} as friend`}
        >
          <Ionicons name="add-outline" size={16} className="text-secondary-foreground" />
          <Text className="text-xs font-semibold text-secondary-foreground" numberOfLines={1}>Add Friend</Text>
        </Button>
      </View>
    </View>
  );
});

SuggestedFriendCard.displayName = 'SuggestedFriendCard';
